import React, { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  ShoppingCart,
  Users,
  UserCheck,
  Package,
  PenTool,
  Grid3x3,
  Trophy,
  Receipt,
  IndianRupee,
  ShieldCheck,
  Warehouse,
  PackageCheck,
  BookOpen,
  RefreshCw,
  X,
  ChevronLeft,
  ChevronRight,
  Target,
  Tag,
} from 'lucide-react';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

const ALL_NAV = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, roles: ['Admin'] },
  { name: 'Plans',     href: '/plans',     icon: Target,           roles: ['Admin'] },
  { name: 'Coupons',  href: '/coupons',   icon: Tag,              roles: ['Admin'] },
  { name: 'Orders',    href: '/orders',    icon: ShoppingCart,     roles: [] },
  { name: 'Leads',     href: '/leads',     icon: Users,            roles: [] },
  { name: 'Replacements',     href: '/replacements',     icon: RefreshCw,  roles: ['Admin'] },
  { name: 'Technicians',      href: '/technicians',      icon: UserCheck,  roles: ['Admin'] },
  { name: 'Products',         href: '/products',         icon: Package,    roles: ['Admin'] },
  { name: 'Product Grid',     href: '/products/grid',    icon: Grid3x3,    roles: ['Admin'] },
  { name: 'Content Creator',  href: '/content-creator',  icon: PenTool,    roles: ['Admin'] },
  { name: 'Contests',         href: '/contests',         icon: Trophy,     roles: ['Admin'] },
  { name: 'Inventory',        href: '/inventory',        icon: Warehouse,  roles: ['Admin'] },
  { name: 'Technician Stock', href: '/technician-stock', icon: PackageCheck, roles: ['Admin'] },
  { name: 'Blogs',     href: '/blogs',     icon: BookOpen,         roles: ['Admin'] },
  { name: 'Expenses',  href: '/expenses',  icon: Receipt,          roles: ['Admin'] },
  { name: 'Payments',  href: '/payments',  icon: IndianRupee,      roles: ['Admin'] },
  { name: 'Users',     href: '/users',     icon: ShieldCheck,      roles: ['Admin'] },
];

function getStoredRoles(): string[] {
  try { return JSON.parse(localStorage.getItem('user') || '{}').roles || []; } catch { return []; }
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose, collapsed = false, onToggleCollapse }) => {
  const location = useLocation();
  const navigation = useMemo(() => {
    const roles = getStoredRoles();
    const isTelecaller = roles.includes('Telecaller') && !roles.includes('Admin');
    if (isTelecaller) {
      // Telecallers only see items with no role restriction (roles: [])
      return ALL_NAV.filter(item => item.roles.length === 0);
    }
    return ALL_NAV;
  }, []);

  return (
    <>
      {/* Mobile backdrop */}
      {isOpen && (
        <div className="fixed inset-0 z-40 lg:hidden" onClick={onClose}>
          <div className="fixed inset-0 bg-slate-950 bg-opacity-70"></div>
        </div>
      )}

      {/* Sidebar */}
      <div className={`
        fixed top-0 left-0 z-50 h-full bg-[#111827] shadow-strong transform transition-all duration-300 ease-in-out
        lg:translate-x-0 border-r border-slate-800
        ${collapsed ? 'w-14' : 'w-60'}
        ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        {/* Header */}
        <div className="flex items-center justify-between h-14 px-3 border-b border-slate-800 bg-[#0f172a]">
          <div className={`flex items-center gap-2.5 overflow-hidden ${collapsed ? 'justify-center w-full' : ''}`}>
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary-500 text-xs font-bold text-white">
              M
            </div>
            {!collapsed && (
              <div>
                <h1 className="text-sm font-semibold tracking-wide text-white">Minto Admin</h1>
                <p className="text-[10px] font-medium uppercase tracking-[0.16em] text-slate-500">Operations</p>
              </div>
            )}
          </div>
          {/* Mobile close */}
          <button onClick={onClose} className="lg:hidden p-2 rounded-md text-slate-400 hover:text-white hover:bg-slate-800 shrink-0">
            <X className="h-4 w-4" />
          </button>
          {/* Desktop collapse toggle */}
          {!isOpen && (
            <button
              onClick={onToggleCollapse}
              className="hidden lg:flex p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-slate-800 shrink-0"
              title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
            </button>
          )}
        </div>

        <nav className="mt-4 px-2">
          {!collapsed && (
            <div className="mb-2 px-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
              Menu
            </div>
          )}
          <div className="space-y-0.5">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href ||
                             (item.href === '/orders' && location.pathname.startsWith('/orders')) ||
                             (item.href === '/blogs' && location.pathname.startsWith('/blogs')) ||
                             (item.href === '/replacements' && location.pathname.startsWith('/replacements')) ||
                             (item.href === '/products' && location.pathname.startsWith('/products') && !location.pathname.startsWith('/products/grid')) ||
                             (item.href === '/products/grid' && location.pathname.startsWith('/products/grid'));
              const Icon = item.icon;

              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={onClose}
                  title={collapsed ? item.name : undefined}
                  className={`sidebar-link w-full ${isActive ? 'active' : ''} ${collapsed ? 'justify-center px-0' : ''}`}
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  {!collapsed && <span className="truncate">{item.name}</span>}
                </Link>
              );
            })}
          </div>
        </nav>
      </div>
    </>
  );
};

export default Sidebar;
